import { MIN_SPACE_DIM_FEET } from 'data/constants';
import { PositionType } from 'data/types';
import {
  boundingBoxOfVertices,
  isPolygonInsideFootprint,
  normalizeVerticesToOrigin,
  polygonOverlapsAny,
  RelativeVertex,
  scaleVertices,
  verticesToAbsolute
} from 'utils/space-polygon';

const SHRINK_STEP = 0.1;
const MIN_SCALE_FLOOR = 0.3;
const SPIRAL_MAX_RADIUS_FEET = 8;

export type FitShapeResult = {
  position: PositionType;
  vertices: RelativeVertex[];
  width: number;
  height: number;
};

export type FitShapeInOpenAreaParams = {
  shapeVertices: RelativeVertex[];
  targetCenter: PositionType;
  footprint: PositionType[] | null;
  otherSpacePolygons: PositionType[][];
};

type SpiralOffset = { dx: number; dy: number };

function buildSpiralOffsets(maxRadiusFeet: number): SpiralOffset[] {
  const offsets: SpiralOffset[] = [];
  for (let dx = -maxRadiusFeet; dx <= maxRadiusFeet; dx += 1) {
    for (let dy = -maxRadiusFeet; dy <= maxRadiusFeet; dy += 1) {
      offsets.push({ dx, dy });
    }
  }
  offsets.sort((a, b) => {
    const distA = a.dx * a.dx + a.dy * a.dy;
    const distB = b.dx * b.dx + b.dy * b.dy;
    return distA - distB;
  });
  return offsets;
}

const SPIRAL_OFFSETS = buildSpiralOffsets(SPIRAL_MAX_RADIUS_FEET);

function isValidPlacement(
  absVerts: PositionType[],
  footprint: PositionType[] | null,
  otherSpacePolygons: PositionType[][]
): boolean {
  if (footprint && footprint.length >= 3) {
    if (!isPolygonInsideFootprint(absVerts, footprint)) return false;
  }
  if (polygonOverlapsAny(absVerts, otherSpacePolygons)) return false;
  return true;
}

function tryFitAtScale(
  baseVertices: RelativeVertex[],
  scale: number,
  targetCenter: PositionType,
  footprint: PositionType[] | null,
  otherSpacePolygons: PositionType[][]
): FitShapeResult | null {
  const scaledVerts = scaleVertices(baseVertices, scale);
  const renormalized = normalizeVerticesToOrigin(scaledVerts);
  const bbox = boundingBoxOfVertices(renormalized.vertices);
  if (bbox.width < MIN_SPACE_DIM_FEET || bbox.height < MIN_SPACE_DIM_FEET)
    return null;

  const idealOriginX = targetCenter.x - bbox.width / 2;
  const idealOriginY = targetCenter.y - bbox.height / 2;

  for (const { dx, dy } of SPIRAL_OFFSETS) {
    const originX = idealOriginX + dx;
    const originY = idealOriginY + dy;
    const absVerts = verticesToAbsolute(renormalized.vertices, {
      x: originX,
      y: originY
    });

    if (isValidPlacement(absVerts, footprint, otherSpacePolygons)) {
      return {
        position: { x: originX, y: originY },
        vertices: renormalized.vertices,
        width: bbox.width,
        height: bbox.height
      };
    }
  }

  return null;
}

export function fitShapeInOpenArea({
  shapeVertices,
  targetCenter,
  footprint,
  otherSpacePolygons
}: FitShapeInOpenAreaParams): FitShapeResult | null {
  if (shapeVertices.length < 3) return null;

  const normalized = normalizeVerticesToOrigin(shapeVertices);
  const baseBbox = boundingBoxOfVertices(normalized.vertices);
  if (baseBbox.width <= 0 || baseBbox.height <= 0) return null;

  const minDim = Math.min(baseBbox.width, baseBbox.height);
  const minScale = Math.max(MIN_SCALE_FLOOR, MIN_SPACE_DIM_FEET / minDim);

  for (let scale = 1; scale >= minScale - 1e-9; scale -= SHRINK_STEP) {
    const result = tryFitAtScale(
      normalized.vertices,
      scale,
      targetCenter,
      footprint,
      otherSpacePolygons
    );
    if (result) return result;
  }

  return null;
}
